<template>
  <div class="container mt-4" :key="selectedTab">
    <template>
      <TopNav
        :title="$t('GENERAL.LABEL.EMAILS')"
        :showOpenMainNav="true"
        :icon="['far', 'users']"
      >
        <NavItem
          icon="far fa-cloud-download"
          :text="$t('MARKETING.LINKS.EXPORT_SUBSCRIBERS')"
          @click.native="exportSubscribers"
        />
        <NavItem
          icon="far fa-plus"
          :text="$t('MARKETING.LINKS.NEW_EMAIL')"
          @click.native="addEmail"
        />
      </TopNav>
      <div>
        <div
          v-if="!isMailServerConnected"
          class="alert-danger px-3 py-2 rounded mb-3"
        >
          <span class="c-charcoal">
            <router-link
              :to="{
                name: 'settings-integrations-smtp',
              }"
            >
              {{ $t('MARKETING.TEXT.MISSING_SMTP_CONNECTION') }}
            </router-link>
          </span>
        </div>
        <HMTabs v-model="selectedTab" class="mt-4 mb-3">
          <HMTab v-for="tab in tabs" :key="tab.name" :name="tab.name">
            <router-link
              class="undecorated-router-link d-flex align-items-center"
              :to="{
                name: 'marketing-emails',
                query: {
                  tab: tab.name,
                },
              }"
            >
              <font-awesome-icon
                :icon="['fas', 'circle']"
                :class="`dot dot--${tab.name}`"
              />
              {{ tab.title }}
            </router-link>
          </HMTab>
        </HMTabs>
        <div class="row">
          <div class="col-12 col-md-8">
            <HMSearch v-model="form.search" />
          </div>
          <div class="col-12 col-md-4">
            <HMDatePicker v-model="form.date" class="flex-grow-1" />
          </div>
        </div>
        <HMPaginatedTable
          name="emails-list-table"
          api="marketing/loadEmails"
          :fields="getSelectedTabFields"
          :query="query"
          class="mt-3"
          ref="emails-list-table"
          :pageInQuery="true"
          :perPageFixed="10"
        >
          <template v-slot:subject="{ data }">
            <router-link :to="`/marketing/emails/${data.id}`" class="d-block">
              <v-clamp autoresize :max-lines="1">
                {{ data.subject || 'Untitled' }}
              </v-clamp>
            </router-link>
          </template>
          <template v-slot:status="{ data }">
            <span :class="`status status--${data.status}`">
              {{ $t(`MARKETING.LABELS.${data.status.toUpperCase()}`) }}
            </span>
          </template>
          <template v-slot:open_rate="{ data }">
            {{ getOpenRate(data) }}
          </template>
          <template v-slot:click_rate="{ data }">
            {{ getClickRate(data) }}
          </template>
          <template v-slot:scheduled="{ data }">
            {{ $formatDate(data.scheduled, 'date-time', '-') }}
          </template>
          <template v-slot:sent="{ data }">
            {{ $formatDate(data.sent, 'date-time', '-') }}
          </template>
          <template v-slot:updated="{ data }">
            {{ $formatDate(data.updated, 'date-time', '-') }}
          </template>
          <template v-slot:created="{ data }">
            {{ $formatDate(data.created, 'date-time', '-') }}
          </template>
          <template slot="edit" slot-scope="{ data }">
            <HMIconButton
              :icon="['far', 'ellipsis-v']"
              :id="`popover-${data.id}`"
            />
            <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
              <HMPopoverMenuItem @click="duplicate(data)">
                {{ $t('MARKETING.LINKS.DUPLICATE') }}
              </HMPopoverMenuItem>
              <HMPopoverMenuItem
                v-if="data.status != 'sent'"
                @click="showEmailDeleteModal(data)"
              >
                <span class="d-flex justify-content-between align-items-center">
                  {{ $t('MARKETING.LINKS.DELETE_EMAIL') }}
                  <i class="far fa-trash-alt c-coral ml-4"></i>
                </span>
              </HMPopoverMenuItem>
            </HMPopover>
          </template>
        </HMPaginatedTable>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { errorToMessage } from '../../../../core/utils/errors';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { get } from 'lodash';
import VClamp from 'vue-clamp';
import HMDialog from '../../../components/common/HMDialog';
import { mapGetters } from 'vuex';

export default {
  name: 'Emails',
  components: {
    TopNav,
    NavItem,
    VClamp,
  },
  computed: {
    ...mapGetters({
      smtp: 'setting/smtp',
      googleMails: 'setting/googleMails',
    }),
    isMailServerConnected() {
      return this.googleMails?.length > 0 || this.isSmtpConnected;
    },
    isSmtpConnected() {
      return this.smtp?.is_smtp_connected;
    },
    query() {
      return {
        q: this.form.search,
        status: this.selectedTab,
        startDate: this.form?.date?.startDate,
        endDate: this.form?.date?.endDate,
      };
    },
    tabs() {
      return [
        {
          name: 'draft',
          title: this.$t('MARKETING.LABELS.DRAFT'),
        },
        {
          name: 'scheduled',
          title: this.$t('MARKETING.LABELS.SCHEDULED'),
        },
        {
          name: 'sent',
          title: this.$t('MARKETING.LABELS.SENT'),
        },
      ];
    },
    statuses() {
      return [
        {
          label: this.$t('MARKETING.LABELS.DRAFT'),
          code: 'draft',
        },
        {
          label: this.$t('MARKETING.LABELS.SCHEDULED'),
          code: 'scheduled',
        },
        {
          label: this.$t('MARKETING.LABELS.SENT'),
          code: 'sent',
        },
      ];
    },
    tableFieldsDraft() {
      return [
        {
          name: 'subject',
          title: this.$t('MARKETING.TABLE.SUBJECT'),
          cssClass: 'h5',
        },
        {
          name: 'status',
          title: this.$t('MARKETING.TABLE.STATUS'),
        },
        {
          name: 'updated',
          title: this.$t('MARKETING.TABLE.UPDATED'),
        },
        {
          name: 'created',
          title: this.$t('MARKETING.TABLE.CREATED'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
    tableFieldsScheduled() {
      return [
        {
          name: 'subject',
          title: this.$t('MARKETING.TABLE.SUBJECT'),
          cssClass: 'h5',
        },
        {
          name: 'status',
          title: this.$t('MARKETING.TABLE.STATUS'),
        },
        {
          name: 'scheduled',
          title: this.$t('MARKETING.TABLE.SCHEDULED'),
        },
        {
          name: 'updated',
          title: this.$t('MARKETING.TABLE.UPDATED'),
        },
        {
          name: 'created',
          title: this.$t('MARKETING.TABLE.CREATED'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
    tableFieldsSent() {
      return [
        {
          name: 'subject',
          title: this.$t('MARKETING.TABLE.SUBJECT'),
          cssClass: 'h5',
        },
        {
          name: 'status',
          title: this.$t('MARKETING.TABLE.STATUS'),
        },
        {
          key: 'num_recipients',
          title: this.$t('MARKETING.TABLE.RECIPIENTS'),
        },
        {
          name: 'open_rate',
          title: this.$t('MARKETING.TABLE.OPEN_RATE'),
        },
        {
          name: 'click_rate',
          title: this.$t('MARKETING.TABLE.CLICK_RATE'),
        },
        {
          key: 'num_unsubscribed',
          title: this.$t('MARKETING.TABLE.UNSUBSCRIBES'),
        },
        {
          name: 'sent',
          title: this.$t('MARKETING.TABLE.SENT'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
    getSelectedTabFields() {
      switch (this.selectedTab) {
        case 'draft':
          return this.tableFieldsDraft;
        case 'scheduled':
          return this.tableFieldsScheduled;
        default:
          return this.tableFieldsSent;
      }
    },
  },
  data() {
    return {
      isLoading: true,
      selectedTab: 'draft',
      form: {
        search: '',
        selectedStatus: '',
        date: {},
      },
    };
  },
  methods: {
    async exportSubscribers() {
      try {
        await this.$store.dispatch(
          'marketing/exportSubscribedStudentsToMarketingEmails'
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to export email subscribers',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async addEmail() {
      try {
        const email = {
          subject: '',
          content: '',
        };
        const newEmail = await this.$store.dispatch(
          'marketing/addEmail',
          email
        );
        this.$router.push({
          name: 'marketing-email-detail',
          params: {
            id: newEmail.id,
          },
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to create a new email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    getOpenRate(email) {
      if (email.num_recipients === 0) return '-';
      const rate = Math.ceil((email.num_opened / email.num_recipients) * 100);
      return `${rate}%`;
    },
    getClickRate(email) {
      if (email.num_recipients === 0) return '-';
      const rate = Math.ceil((email.num_clicked / email.num_recipients) * 100);
      return `${rate}%`;
    },
    async duplicate(email) {
      try {
        await this.$store.dispatch('marketing/duplicateEmail', email.id);
        this.$refs['emails-list-table'].refresh();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to delete email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async showEmailDeleteModal(email) {
      if (this.isDirty) await this.save(true);
      this.$modal.show(HMDialog, {
        title: this.$t('MARKETING.TITLES.DELETE_EMAIL'),
        text: this.$t('MARKETING.TEXT.CONFIRM_DELETE_EMAIL', {
          email_subject: email.subject,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.DELETE_EMAIL'),
        confirm: async () => {
          await this.remove(email);
        },
      });
    },
    async remove(email) {
      try {
        await this.$store.dispatch('marketing/deleteEmail', email.id);
        this.$refs['emails-list-table'].refresh();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to delete email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
  },
  async mounted() {
    this.selectedTab = this.$route.query.tab || 'draft';
    await this.$store.dispatch('setting/getGoogleMailConnections');
    await this.$store.dispatch('setting/getSmtpInfo');
  },
  watch: {
    '$route.query.tab': {
      handler: function (tab) {
        this.selectedTab = tab || 'draft';
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.status {
  padding: 4px 8px;
  border-radius: $border-radius;
  &--draft {
    background-color: lighten($coral, 29%);
    color: $coral;
  }
  &--sent {
    background-color: lighten($light-green, 15%);
    color: $forest;
  }
  &--scheduled {
    background-color: lighten($brown, 25%);
    color: $white;
  }
}

.dot {
  margin-right: 6px;
  font-size: 0.5rem;
  &--sent {
    color: $light-green;
  }
  &--scheduled {
    color: $brown;
  }
  &--draft {
    color: $light-bubblegum;
  }
}
</style>
